import type { DocumentTemplateCreationFailedAction } from '@Application/Documents/Templates/Actions/documentTemplateCreationFailed';
import FetchDocumentTemplateVersion from '@Application/Documents/Templates/Queries/FetchDocumentTemplateVersion';
import fetchDocumentTemplateVersion from '@Application/Documents/Templates/fetchDocumentTemplateVersion';
import type { DocumentTemplateVersionUploadFailedAction } from '@Application/Documents/Templates/Actions/documentTemplateVersionUploadFailed';
import CreateDocumentTemplate from '@Application/Documents/Templates/Commands/CreateDocumentTemplate';
import UploadDocumentTemplateVersion from '@Application/Documents/Templates/Commands/UploadDocumentTemplateVersion';
import createDocumentTemplate from '@Application/Documents/Templates/createDocumentTemplate';
import uploadDocumentTemplateVersion from '@Application/Documents/Templates/uploadDocumentTemplateVersion';
import type { DocumentTemplateFileDescriptionFailedAction } from '@Application/Documents/TemplateFiles/Actions/documentTemplateFileDescriptionFailed';
import DescribeDocumentTemplateFile from '@Application/Documents/TemplateFiles/Queries/DescribeDocumentTemplateFile';
import describeDocumentTemplateFile from '@Application/Documents/TemplateFiles/describeDocumentTemplateFile';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import FetchTemplateFieldTypes from '@Application/Documents/TemplateFieldTypes/Queries/FetchTemplateFieldTypes';
import fetchTemplateFieldTypes from '@Application/Documents/TemplateFieldTypes/fetchTemplateFieldTypes';
import documentTemplateUploadSidebarSignal, {
	closeDocumentTemplateUploadSidebar,
} from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTemplateUploadSidebarSignal';
import DocumentTemplateFilePreview from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateUploadSidebar/DocumentTemplateFilePreview';
import DocumentTemplateUploadFormData from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateUploadSidebar/FormData/DocumentTemplateUploadFormData';
import resolveTemplateFieldTypeLabel from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateUploadSidebar/Helpers/resolveTemplateFieldTypeLabel';
import DocumentTemplateFilePreviewView from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateUploadSidebar/Views/DocumentTemplateFilePreviewView';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import { Button, Dropdown, Form, InputText, Message, Sidebar, TextArea } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { fieldMarginStyles } from '@Guidelines/Theme/commonStyles';

const hiddenInputStyle = css`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

const fileRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const fileErrorStyle = css`
	margin: 0.375rem 0 0;
`;

/**
 * One panel for both deposit modes, told apart by `context.mode` on the signal: `create` renders
 * `name`/`documentTypeId`/`description` in addition to the file picker, `version` renders the file
 * picker alone — a version never carries the other fields (they are edited separately, through
 * `DocumentTemplateEditSidebar`). The local preview (`describeDocumentTemplateFile`) is kept as
 * component state, never in the store: no other screen reads it.
 */
const DocumentTemplateUploadSidebar: React.FC = () => {
	const dispatch = useDispatch();
	const context = useSignalValue(documentTemplateUploadSidebarSignal);
	const { currentOrganization } = useCurrentOrganization();

	const { documentTypes } = useAccountDocumentTypes();

	const { data: templateFieldTypes } = useStoreQuery({
		selector: (state) => state.templateFieldTypesStore.templateFieldTypes,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchTemplateFieldTypes(new FetchTemplateFieldTypes())),
	});

	const [formData, setFormData] = React.useState(() =>
		DocumentTemplateUploadFormData.createEmpty('create'),
	);
	const [preview, setPreview] = React.useState<DocumentTemplateFilePreviewView | null>(null);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [serverFileError, setServerFileError] = React.useState<string | null>(null);
	const [currentVersionSectionKeys, setCurrentVersionSectionKeys] = React.useState<
		readonly string[] | null
	>(null);
	const [currentVersionStatus, setCurrentVersionStatus] = React.useState<
		'idle' | 'loading' | 'loaded' | 'failed'
	>('idle');

	const formId = React.useId();
	const fileInputId = React.useId();
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const mode = context?.mode ?? 'create';
	const templateId = context?.mode === 'version' ? context.templateId : null;
	const templates = useSelector((state) => state.documentTemplatesStore.templates);
	const currentVersionId = templates?.findById(templateId)?.currentVersionId ?? null;
	const organizationId = currentOrganization?.id ?? null;

	// Each mode always starts from a blank slate: the deposit sidebar never prefills from an existing
	// template, even in `version` mode (only the file changes; name/description are edited elsewhere).
	React.useEffect(() => {
		if (context === null) {
			return;
		}

		setFormData(DocumentTemplateUploadFormData.createEmpty(context.mode));
		setPreview(null);
		setServerFileError(null);
	}, [context]);

	// The reevaluation sections of the version in force seed the boxes of the new file. A missing
	// template or a failed read never blocks the deposit: the person is warned and checks the boxes.
	React.useEffect(() => {
		setCurrentVersionSectionKeys(null);

		if (templateId === null) {
			setCurrentVersionStatus('idle');
			return;
		}

		if (organizationId === null || currentVersionId === null) {
			setCurrentVersionStatus('failed');
			return;
		}

		let isCancelled = false;
		setCurrentVersionStatus('loading');

		dispatch(
			fetchDocumentTemplateVersion(new FetchDocumentTemplateVersion(organizationId, currentVersionId)),
		)
			.then((action) => {
				if (isCancelled || action.type !== 'DOCUMENT_TEMPLATE_VERSION_FETCHED') {
					return;
				}

				setCurrentVersionSectionKeys(action.payload.reevaluationSectionKeys);
				setCurrentVersionStatus('loaded');
			})
			.catch(() => {
				if (!isCancelled) {
					setCurrentVersionStatus('failed');
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [templateId, organizationId, currentVersionId, dispatch]);

	// Only keys that still exist as top-level sections of the new file are carried over. A user's toggle
	// changes neither dependency, so it is never overwritten.
	React.useEffect(() => {
		if (currentVersionSectionKeys === null || preview === null || !preview.isDeclared) {
			return;
		}

		const markableKeys = preview.sections
			.filter((section) => section.isReevaluationMarkable)
			.map((section) => section.key);

		setFormData((current) =>
			current.setReevaluationSectionKeys(
				currentVersionSectionKeys.filter((key) => markableKeys.includes(key)),
			),
		);
	}, [currentVersionSectionKeys, preview]);

	const handleToggleReevaluationSection = React.useCallback((key: string) => {
		setFormData((current) => current.toggleReevaluationSection(key));
	}, []);

	const handleNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setName(value));
	}, []);

	const handleDocumentTypeChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setDocumentTypeId(value));
	}, []);

	const handleDescriptionChange = React.useCallback((value: string) => {
		setFormData((current) => current.setDescription(value));
	}, []);

	const handleBrowseClick = React.useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleFileInputChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];

			// Cleared so the same file can be re-selected after a rejection (a native input keeps its
			// previous value otherwise, and `change` never fires again for the identical path).
			event.target.value = '';

			if (file === undefined) {
				return;
			}

			setFormData((current) => current.setFile(file));
			setServerFileError(null);
			setPreview(DocumentTemplateFilePreviewView.loading());

			dispatch(describeDocumentTemplateFile(new DescribeDocumentTemplateFile(file)))
				.then((action) => {
					// `describeDocumentTemplateFile` only ever resolves with this action type; a failure
					// rejects the promise instead (handled below) — this guard only narrows the union for
					// the compiler.
					if (action.type !== 'DOCUMENT_TEMPLATE_FILE_DESCRIBED') {
						return;
					}

					const nextPreview = DocumentTemplateFilePreviewView.fromDescribedPayload(
						action.payload,
						(value) => resolveTemplateFieldTypeLabel(value, templateFieldTypes),
					);
					setPreview(nextPreview);
					setFormData((current) => current.setIsFileDeclared(nextPreview.isDeclared));
				})
				.catch((action: DocumentTemplateFileDescriptionFailedAction) => {
					setPreview(
						DocumentTemplateFilePreviewView.readFailed(
							action.payload?.message ?? "Le fichier n'a pas pu être lu.",
						),
					);
					setFormData((current) => current.setIsFileDeclared(false));
				});
		},
		[dispatch, templateFieldTypes],
	);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Unreachable in practice: the organization screen never mounts this sidebar without a current
		// organization, and the form cannot be valid without a selected, declared file.
		if (currentOrganization === null || formData.file === null) {
			return;
		}

		const depositOrganizationId = currentOrganization.id;
		const file = formData.file;

		setIsSubmitting(true);

		if (mode === 'version' && templateId !== null) {
			dispatch(
				uploadDocumentTemplateVersion(
					new UploadDocumentTemplateVersion(
						depositOrganizationId,
						templateId,
						file,
						formData.reevaluationSectionKeys,
					),
				),
			)
				.then(() => {
					closeDocumentTemplateUploadSidebar();
				})
				.catch((action: DocumentTemplateVersionUploadFailedAction) => {
					updateValidationFromExternalSource({
						action,
						eventNames: 'DOCUMENT_TEMPLATE_VERSION_UPLOAD_FAILED',
						formDataUpdater: setFormData,
					});
					// Only a payload with no `fieldErrors` (a `422`/`413`) belongs under the file picker; a
					// `400` with `fieldErrors` — `name`/`documentTypeId`/`file` included — is already routed to
					// its own field by `updateValidationFromExternalSource` above.
					setServerFileError(
						action.payload !== undefined && action.payload.fieldErrors.length === 0
							? action.payload.message
							: null,
					);
				})
				.finally(() => setIsSubmitting(false));
			return;
		}

		// Unreachable in practice: `isFormDataValid()` above already rejects a `null` `documentTypeId`
		// in create mode (`isFilledWhenCreating`).
		if (formData.documentTypeId === null) {
			setIsSubmitting(false);
			return;
		}

		const description = formData.description === '' ? null : formData.description;

		dispatch(
			createDocumentTemplate(
				new CreateDocumentTemplate(
					depositOrganizationId,
					formData.name,
					formData.documentTypeId,
					description,
					file,
					formData.reevaluationSectionKeys,
				),
			),
		)
			.then(() => {
				closeDocumentTemplateUploadSidebar();
			})
			.catch((action: DocumentTemplateCreationFailedAction) => {
				updateValidationFromExternalSource({
					action,
					eventNames: 'DOCUMENT_TEMPLATE_CREATION_FAILED',
					formDataUpdater: setFormData,
				});
				// Only a payload with no `fieldErrors` (a `422`/`413`) belongs under the file picker; a
				// `400` with `fieldErrors` — `name`/`documentTypeId`/`file` included — is already routed to
				// its own field by `updateValidationFromExternalSource` above.
				setServerFileError(
					action.payload !== undefined && action.payload.fieldErrors.length === 0
						? action.payload.message
						: null,
				);
			})
			.finally(() => setIsSubmitting(false));
	}, [formData, currentOrganization, mode, templateId, dispatch]);

	// The client-side/`400` field error takes priority; a `422`/`413` carries no `fieldErrors` and only
	// ever reaches this fallback — the file and body errors are never merged under the same message.
	const fileErrorMessage = formData.getValidationMessage('file') ?? serverFileError ?? undefined;

	const header = mode === 'version' ? 'Déposer une nouvelle version' : 'Déposer un gabarit';

	return (
		<Sidebar
			visible={context !== null}
			onHide={closeDocumentTemplateUploadSidebar}
			position="right"
			header={header}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeDocumentTemplateUploadSidebar}
					/>
					<Button
						type="submit"
						form={formId}
						label="Déposer"
						loading={isSubmitting}
						disabled={
							preview === null || !preview.isDeclared || currentVersionStatus === 'loading'
						}
					/>
				</React.Fragment>
			}
			blockScroll
		>
			<Form id={formId} onSubmit={handleSubmit} aria-label={header}>
				<ConditionalLoader data={mode === 'create'}>
					<InputText
						name="name"
						label="Nom du gabarit"
						required
						value={formData.name}
						onChange={handleNameChange}
						errorMessage={formData.getValidationMessage('name')}
						isFormFieldValid={formData.isValid('name')}
					/>
					<Dropdown<number>
						name="documentTypeId"
						label="Type de document"
						required
						loading={documentTypes === null}
						options={documentTypes?.activeTypes() ?? []}
						optionLabel="label"
						optionValue="id"
						value={formData.documentTypeId}
						onChange={handleDocumentTypeChange}
						errorMessage={formData.getValidationMessage('documentTypeId')}
						isFormFieldValid={formData.isValid('documentTypeId')}
					/>
					<TextArea
						name="description"
						label="Description"
						value={formData.description}
						onChange={handleDescriptionChange}
						errorMessage={formData.getValidationMessage('description')}
						isFormFieldValid={formData.isValid('description')}
					/>
				</ConditionalLoader>

				<div css={fieldMarginStyles}>
					<div css={fileRowStyle}>
						<Button type="button" label="Choisir un fichier" onClick={handleBrowseClick} />

						<ConditionalLoader data={formData.file}>
							{(file) => <span>{file.name}</span>}
						</ConditionalLoader>
					</div>
					<label css={hiddenInputStyle} htmlFor={fileInputId}>
						Choisir un fichier .docx
					</label>
					<input
						ref={fileInputRef}
						id={fileInputId}
						css={hiddenInputStyle}
						type="file"
						accept=".docx"
						onChange={handleFileInputChange}
					/>

					<ConditionalLoader data={fileErrorMessage}>
						<small css={fileErrorStyle}>{fileErrorMessage}</small>
					</ConditionalLoader>
				</div>

				{mode === 'version' && currentVersionStatus === 'failed' ? (
					<div css={fieldMarginStyles}>
						<Message
							severity="warn"
							text="Les sections de réévaluation de la version en vigueur n'ont pas pu être chargées : vérifiez les cases avant de déposer."
						/>
					</div>
				) : null}

				<ConditionalLoader data={preview}>
					{(preview) => (
						<div css={[fieldMarginStyles, { marginTop: '1.5rem' }]}>
							<DocumentTemplateFilePreview
								preview={preview}
								reevaluationSectionKeys={formData.reevaluationSectionKeys}
								onToggleReevaluationSection={handleToggleReevaluationSection}
							/>
						</div>
					)}
				</ConditionalLoader>
			</Form>
		</Sidebar>
	);
};

export default DocumentTemplateUploadSidebar;
